import {
  transformResponseToUserTemplateViewModel,
  transformViewModelIntoUserTemplateRequest,
} from "@/services/userTemplateService.mapper";
import { UserTemplateServiceViewModel } from "@/services/userTemplateService.types";
import { useCallback, useEffect, useRef, useState } from "react";

import { usePreferences } from "./usePreferences";

type UseTemplateParams = {
  templateId?: string;
};

type HandleTemplateChange = <K extends keyof UserTemplateServiceViewModel>(
  field: K,
  value: UserTemplateServiceViewModel[K]
) => Promise<void>;

type UseUserTemplateResult = {
  userTemplate: UserTemplateServiceViewModel | undefined;
  updateUserTemplate: HandleTemplateChange;
};

export function useUserTemplate({ templateId }: UseTemplateParams): UseUserTemplateResult {
  const { fetchReportStyle, updateReportStyle } = usePreferences();
  const [userTemplateViewModel, setUserTemplateViewModel] = useState<
    UserTemplateServiceViewModel | undefined
  >(undefined);
  const userTemplateViewModelRef = useRef<UserTemplateServiceViewModel | undefined>(undefined);

  useEffect(() => {
    if (!templateId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchReportStyle(templateId);
        if (cancelled) return;
        const { report_style } = response;
        if (report_style == null) return;
        const transformed = transformResponseToUserTemplateViewModel(report_style);
        userTemplateViewModelRef.current = transformed;
        setUserTemplateViewModel(transformed);
      } catch (error) {
        console.error("Error loading Template :", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const updateUserTemplate: HandleTemplateChange = useCallback(
    async <K extends keyof UserTemplateServiceViewModel>(
      field: K,
      value: UserTemplateServiceViewModel[K]
    ) => {
      if (!templateId || !userTemplateViewModelRef.current) return;
      try {
        const next = { ...userTemplateViewModelRef.current, [field]: value };
        userTemplateViewModelRef.current = next;
        setUserTemplateViewModel(next);
        await updateReportStyle(templateId, transformViewModelIntoUserTemplateRequest(next));
      } catch (error) {
        console.error("Error updating report style:", error);
      }
    },
    [templateId, updateReportStyle]
  );

  return {
    userTemplate: userTemplateViewModel,
    updateUserTemplate,
  };
}
